import React, { useState } from 'react';
import { X, Loader2, Clock } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HoursModal = ({ isOpen, onClose, task, onSuccess }) => {
  const { token, user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [hours, setHours] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hours || parseFloat(hours) <= 0) {
      alert('Please enter valid hours');
      return;
    }

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      console.log('Registering hours:', {
        task_id: task.id,
        hours: parseFloat(hours),
        date: date,
        user_id: user.id
      });
      
      // Register hours via API
      const response = await axios.post(`${API}/hours`, {
        field_182: [task.id], // Task connection (CORRECT FIELD!)
        field_36: parseFloat(hours), // Hours worked (field_36 not field_112)
        field_37: date, // Date (field_37 not field_113)
        field_38: notes, // Notes/description (field_38 not field_114)
        field_62: [user.id] // User who logged the hours
      }, { headers });
      
      console.log('Hours registered successfully:', response.data);
      console.log('Task before refresh - registered hours:', task.field_183_raw);

      // Wait for Knack to recalculate aggregations (connection sum fields)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('Calling onSuccess to refresh task list...');
      onSuccess?.();
      onClose();
      // Reset form
      setHours('');
      setNotes('');
      setDate(new Date().toISOString().split('T')[0]);
    } catch (error) {
      console.error('Failed to register hours:', error);
      console.error('Error details:', error.response?.data);
      alert(`Failed to register hours: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !task) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Register Hours</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {task.field_43_raw || 'Untitled Task'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Current Hours */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Registered Hours:</span>
              <span className="text-lg font-bold text-gray-900 dark:text-white">{task.field_183_raw || 0} hrs</span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Expected Hours:</span>
              <span className="text-lg font-bold text-gray-900 dark:text-white">{task.field_45_raw || 0} hrs</span>
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>

          {/* Hours */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Hours Worked <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="number"
                min="0.5"
                step="0.5"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="0.0"
                required
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              New total: {((task.field_183_raw || 0) + parseFloat(hours || 0)).toFixed(1)} hrs
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="What did you work on?"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !hours || parseFloat(hours) <= 0}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition flex items-center justify-center"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Register Hours'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HoursModal;
